import { Flex } from "@redotech/redo-web/flex";
import { memo, useLayoutEffect, useMemo, useRef } from "react";
import {
  clampEmailMessageIframeHeight,
  getCachedEmailMessageHeight,
  getEmailMessageHeightCacheKey,
  prepareEmailMessageContent,
  setCachedEmailMessageHeight,
  type MessageContent,
} from "./email-message-rendering";
import * as styles from "./email-message.module.css";

export type { MessageContent } from "./email-message-rendering";

export interface SimpleEmailMessageProps {
  className?: string;
  heightCacheKey?: string | null;
  messageContent: MessageContent;
}

export const SimpleEmailMessage = memo(function SimpleEmailMessage({
  className,
  heightCacheKey,
  messageContent,
}: SimpleEmailMessageProps) {
  const emailIframeRef = useRef<HTMLIFrameElement>(null);
  const messageContentType = messageContent.type;
  const messageValue =
    messageContent.type === "messageHtml"
      ? messageContent.messageHtml
      : messageContent.messageText;

  const { estimatedHeight, iframeContent } = useMemo(
    () =>
      prepareEmailMessageContent(
        messageContentType === "messageHtml"
          ? { type: "messageHtml", messageHtml: messageValue }
          : { type: "messageText", messageText: messageValue },
      ),
    [messageContentType, messageValue],
  );

  useLayoutEffect(() => {
    const iframe = emailIframeRef.current;
    if (!iframe) {
      return;
    }

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) {
      return;
    }

    const getCurrentHeightCacheKey = () =>
      getEmailMessageHeightCacheKey(heightCacheKey, iframe.clientWidth);

    let animationFrame = 0;
    let lastHeight =
      getCachedEmailMessageHeight(getCurrentHeightCacheKey()) ??
      estimatedHeight;
    iframe.style.height = `${lastHeight}px`;

    const updateIframeHeight = () => {
      if (!iframeDoc.body) {
        return;
      }

      const measuredHeight = Math.max(
        iframeDoc.body.scrollHeight,
        iframeDoc.body.offsetHeight,
      );
      const nextHeight = clampEmailMessageIframeHeight(measuredHeight);
      setCachedEmailMessageHeight(getCurrentHeightCacheKey(), nextHeight);

      if (Math.abs(nextHeight - lastHeight) > 1) {
        lastHeight = nextHeight;
        iframe.style.height = `${nextHeight}px`;
      }
    };

    const scheduleIframeHeightUpdate = () => {
      if (animationFrame !== 0) {
        return;
      }

      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = 0;
        updateIframeHeight();
      });
    };

    iframeDoc.open();
    iframeDoc.write(iframeContent);
    iframeDoc.close();
    hardenIframeDocument(iframeDoc);
    updateIframeHeight();

    const iframeWindow = iframe.contentWindow;
    iframeWindow?.addEventListener("load", scheduleIframeHeightUpdate);

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(scheduleIframeHeightUpdate);
    if (resizeObserver) {
      resizeObserver.observe(iframe);
      resizeObserver.observe(iframeDoc.documentElement);
      if (iframeDoc.body) {
        resizeObserver.observe(iframeDoc.body);
      }
    }

    const images = Array.from(iframeDoc.images);
    for (const image of images) {
      image.decoding = "async";
      image.loading = "lazy";
      image.referrerPolicy = "no-referrer";
      image.addEventListener("load", scheduleIframeHeightUpdate);
      image.addEventListener("error", scheduleIframeHeightUpdate);
    }

    scheduleIframeHeightUpdate();

    return () => {
      if (animationFrame !== 0) {
        window.cancelAnimationFrame(animationFrame);
      }
      iframeWindow?.removeEventListener("load", scheduleIframeHeightUpdate);
      resizeObserver?.disconnect();
      for (const image of images) {
        image.removeEventListener("load", scheduleIframeHeightUpdate);
        image.removeEventListener("error", scheduleIframeHeightUpdate);
      }
    };
  }, [estimatedHeight, heightCacheKey, iframeContent]);

  return (
    <Flex className={className} w="full">
      <iframe
        className={styles.emailIframe}
        ref={emailIframeRef}
        referrerPolicy="no-referrer"
        sandbox="allow-downloads allow-popups allow-popups-to-escape-sandbox allow-same-origin"
        title="Email content"
      />
    </Flex>
  );
});

function hardenIframeDocument(iframeDoc: Document): void {
  for (const baseElement of iframeDoc.querySelectorAll("base")) {
    baseElement.remove();
  }

  const links = iframeDoc.querySelectorAll("a[href]");
  for (const link of links) {
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
    link.setAttribute("referrerpolicy", "no-referrer");
  }
}
